import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { RiToggleLine } from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { ReactNode, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";

import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import {
  IcoFeature,
  IcoRemoteConfig,
  IcoSTARSFunnel,
} from "@/common/components/Icons";
import ModalCancelButton from "@/common/components/ModalCancelButton";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import useApiForm from "@/common/hooks/useApiForm";
import api from "@/common/utils/api";
import { CreateAffectEnvironments } from "@/environment/components/EnvironmentCallouts";
import { NewFeatureForm } from "@/feature/components/NewFeatureForm";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import { useFeatureKeys } from "@/feature/data/useFeatureData";
import { useFlag } from "@reflag/react-sdk";
import {
  CreateFeatureArgsSchema,
  CreateFeatureArgsType,
  FeatureDetail,
} from "@reflag/shared/featureAPI";
import { FeatureUrl } from "@reflag/shared/urls";
import { KeyFormatPatterns } from "@reflag/shared/utils/keyFormat";

export default function NewFeature({
  isOpen = true,
  footerDetails,
  onClose,
  onSuccess,
  ...props
}: {
  isOpen?: boolean;
  footerDetails?: ReactNode;
  onClose?: () => void;
  onSuccess?: (feature: FeatureDetail) => void;
} & Omit<ModalProps, "children" | "isOpen" | "onClose">) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const asideTextColor = useColorModeValue("gray.900", "gray.100");
  const asideIconColor = useColorModeValue("brand.500", "brand.400");
  const asideBgColor = useColorModeValue("gray.50", "gray.850");

  const { isEnabled: isDynamicConfigsEnabled } = useFlag("dynamic-configs");

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      onClose={() => {
        if (onClose) {
          onClose();
        } else {
          navigate({
            pathname: "..",
            search: searchParams.toString(),
          });
        }
      }}
      {...props}
    >
      <ModalOverlay />
      <ModalContent
        alignItems="stretch"
        bg="appBackground"
        display="flex"
        flexDirection="row"
        gap={0}
        justifyContent="stretch"
        maxW="auto"
        pt={0}
        w="auto"
      >
        <Flex direction="column" gap={6} pt={6} w="sm">
          <ModalHeader>
            <VStack align="start">
              <HStack>
                <IcoFeature size="2xl" />
                <Heading>New flag</Heading>
              </HStack>
            </VStack>
          </ModalHeader>
          <NewFeatureBody
            footerDetails={footerDetails}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        </Flex>
        <Flex
          align="center"
          bg={asideBgColor}
          borderLeft="1px solid"
          borderLeftColor="appBorder"
          borderRightRadius="md"
          flexGrow={1}
          p={6}
          pos="relative"
          w="3xs"
        >
          <ModalCloseButton />
          <Flex as="ul" direction="column" fontSize="sm" gap={6}>
            <Flex as="li" direction="row" gap={2} m={0} p={0}>
              <Box color={asideIconColor} pt={0.5}>
                <RiToggleLine size={16} />
              </Box>
              <Flex direction="column" gap={1}>
                <Text color={asideTextColor} fontWeight="medium">
                  Feature access
                </Text>
                <Text color="dimmed" sx={{ textWrap: "balance" }}>
                  Safely roll-out features gradually
                </Text>
              </Flex>
            </Flex>
            <Flex as="li" direction="row" gap={2} m={0} p={0}>
              <Box color={asideIconColor} pt={0.5}>
                <IcoSTARSFunnel size="md" />
              </Box>
              <Flex direction="column" gap={1}>
                <Text color={asideTextColor} fontWeight="medium">
                  Launch monitoring
                </Text>
                <Text color="dimmed" sx={{ textWrap: "balance" }}>
                  Track exposure, adoption and get feedback
                </Text>
              </Flex>
            </Flex>
            {isDynamicConfigsEnabled && (
              <Flex as="li" direction="row" gap={2} m={0} p={0}>
                <Box color={asideIconColor} pt={0.5}>
                  <IcoRemoteConfig size="md" />
                </Box>
                <Flex direction="column" gap={1}>
                  <Text color={asideTextColor} fontWeight="medium">
                    Remote config
                  </Text>
                  <Text color="dimmed" sx={{ textWrap: "balance" }}>
                    Change app behavior without re-deploys
                  </Text>
                </Flex>
              </Flex>
            )}
          </Flex>
        </Flex>
      </ModalContent>
    </Modal>
  );
}

export function NewFeatureBody({
  footerDetails,
  onClose,
  onSuccess,
}: {
  footerDetails?: ReactNode;
  onClose?: () => void;
  onSuccess?: (feature: FeatureDetail) => void;
}) {
  const { app, env, org, user } = useEnvironment();
  const { data: existingKeys = [] } = useFeatureKeys();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const cancelHoverColor = useColorModeValue("gray.700", "gray.300");
  const createFeatureArgsRefinedSchema = useMemo(() => {
    return CreateFeatureArgsSchema.superRefine(({ key }, ctx) => {
      if (!key) {
        return;
      }

      if (!KeyFormatPatterns[org.featureKeyFormat].validate(key)) {
        ctx.addIssue({
          code: "custom",
          path: ["key"],
          message: `Key must match the "${
            KeyFormatPatterns[org.featureKeyFormat].label
          }" format`,
        });
      }

      if (existingKeys.includes(key)) {
        ctx.addIssue({
          code: "custom",
          path: ["key"],
          message: "This key is already in use",
        });
      }
    });
  }, [org, existingKeys]);

  const { form, handleSubmit } = useApiForm(
    async (formData: CreateFeatureArgsType) => {
      formData.ownerUserId = user?.id;

      return api
        .post<"/apps/:appId/features">(`/apps/${app.id}/features`, formData, {
          params: { envId: env.id },
        })
        .then((response) => response.data.feature);
    },
    createFeatureArgsRefinedSchema,
    {
      onSuccess: (feature) => {
        queryClient.invalidateQueries({
          queryKey: featureQueryKeys.listNames(app.id),
        });
        queryClient.setQueryData(
          featureQueryKeys.single(app.id, feature.id),
          feature,
        );
        queryClient.setQueryData(
          featureQueryKeys.single(app.id, env.id, feature.id),
          feature,
        );

        queryClient.invalidateQueries({
          queryKey: featureQueryKeys.list(app.id),
          exact: true,
        });

        if (onSuccess) {
          onSuccess(feature);
        } else {
          navigate(FeatureUrl(env, feature));
        }
      },
    },
    {
      defaultValues: {
        source: "event",
        name: "",
        key: "",
      },
    },
  );

  return (
    <FormProvider {...form}>
      <ModalBody>
        <NewFeatureForm id="new-feature" onSubmit={handleSubmit} />
        <FormRootError />
        <CreateAffectEnvironments entity="flags" mt={8} />
      </ModalBody>
      <ModalFooter bg="transparent" justifyContent="space-between">
        {typeof footerDetails === "string" ? (
          <Text color="dimmed" fontStyle="italic">
            {footerDetails}
          </Text>
        ) : (
          <Box>{footerDetails}</Box>
        )}
        <ButtonGroup>
          {onClose ? (
            <Button
              _hover={{ color: cancelHoverColor }}
              type="button"
              variant="ghostDimmed"
              onClick={onClose}
            >
              Cancel
            </Button>
          ) : (
            <ModalCancelButton shouldGoBack={false} />
          )}
          <FormSubmit form="new-feature" loadingText="Creating...">
            Create
          </FormSubmit>
        </ButtonGroup>
      </ModalFooter>
    </FormProvider>
  );
}
